import React, { useState, useEffect } from "react"
import { Handle, Position } from "reactflow"
import { Eye, EyeOff, ChevronDown, ChevronRight } from "lucide-react"
import { Button } from "@aix/ui"

interface Column {
  id: string
  name: string
  dataType: string
  isPrimaryKey: boolean
  isForeignKey: boolean
  isNullable: boolean
  description?: string | null
}

interface TableNodeData {
  id: string
  name: string
  schema: string
  description?: string | null
  columns: Column[]
  onViewDetails: (tableId: string) => void
  // Feature I: Column hover
  onColumnHover?: (columnId: string | null, relationshipIds: string[]) => void
  relationshipLookup?: Map<string, string[]>
  // Feature III: Relationship badge
  relationshipCount?: number
  onBadgeClick?: (tableId: string) => void
  isBadgeActive?: boolean
}

interface TableNodeProps {
  data: TableNodeData
}

// Feature III: Relationship Badge Component
const RelationshipBadge: React.FC<{
  count: number
  onClick: () => void
  isActive: boolean
}> = ({ count, onClick, isActive }) => {
  const getBadgeColor = () => {
    if (count === 0) return "text-muted-foreground bg-muted"
    if (count <= 3) return "text-chart-2 bg-chart-2/10"
    return "text-primary bg-primary/10"
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={`
        text-xs px-1.5 py-0.5 rounded font-mono
        hover:opacity-80 transition-all
        ${getBadgeColor()}
        ${isActive ? "ring-2 ring-offset-1 ring-primary animate-pulse" : ""}
      `}
      title={`${count} relationship${count !== 1 ? "s" : ""} - Click to highlight`}
    >
      ↔{count}
    </button>
  )
}

const getColumnIcon = (column: Column) => {
  if (column.isPrimaryKey) return "🔑"
  if (column.isForeignKey) return "🔗"

  const type = column.dataType.toLowerCase()
  if (type.includes("varchar") || type.includes("text") || type.includes("char")) return "📝"
  if (type.includes("int") || type.includes("numeric") || type.includes("decimal") || type.includes("float"))
    return "📊"
  if (type.includes("timestamp") || type.includes("date") || type.includes("time")) return "📅"
  if (type.includes("bool")) return "🔒"
  if (type.includes("json")) return "📦"
  if (type.includes("uuid")) return "🆔"

  return "•"
}

const sortColumns = (columns: Column[]): Column[] => {
  // Sort: Primary keys first, then foreign keys, then regular columns
  return [...columns].sort((a, b) => {
    if (a.isPrimaryKey && !b.isPrimaryKey) return -1
    if (!a.isPrimaryKey && b.isPrimaryKey) return 1
    if (a.isForeignKey && !b.isForeignKey) return -1
    if (!a.isForeignKey && b.isForeignKey) return 1
    return a.name.localeCompare(b.name)
  })
}

const TableNode: React.FC<TableNodeProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Column visibility state - stored in localStorage per table
  const storageKey = `databrain-column-visibility-${data.id}`
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(storageKey)
    return stored ? new Set(JSON.parse(stored)) : new Set()
  })

  // Save visibility state to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(Array.from(hiddenColumnIds)))
  }, [hiddenColumnIds, storageKey])

  const toggleColumnVisibility = (columnId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setHiddenColumnIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(columnId)) {
        newSet.delete(columnId)
      } else {
        newSet.add(columnId)
      }
      return newSet
    })
  }

  const sortedColumns = sortColumns(data.columns)

  // Separate visible and hidden columns
  const visibleColumnsList = sortedColumns.filter((col) => !hiddenColumnIds.has(col.id))
  const hiddenColumnsList = sortedColumns.filter((col) => hiddenColumnIds.has(col.id))

  // If no visibility preferences set, show first 5
  const hasVisibilityPreferences = hiddenColumnIds.size > 0
  const defaultVisibleCount = 5

  let columnsToShow: Column[]
  let columnsInExpanded: Column[]

  if (hasVisibilityPreferences) {
    // User has set preferences - respect them
    columnsToShow = isExpanded ? visibleColumnsList : visibleColumnsList.slice(0, defaultVisibleCount)
    columnsInExpanded = isExpanded ? hiddenColumnsList : visibleColumnsList.slice(defaultVisibleCount)
  } else {
    // No preferences - show first 5 by default
    columnsToShow = isExpanded ? sortedColumns : sortedColumns.slice(0, defaultVisibleCount)
    columnsInExpanded = []
  }

  const hiddenCount = hasVisibilityPreferences
    ? hiddenColumnsList.length + Math.max(0, visibleColumnsList.length - defaultVisibleCount)
    : Math.max(0, sortedColumns.length - defaultVisibleCount)

  return (
    <div className="bg-background border-2 border-border rounded-lg shadow-lg min-w-[280px] max-w-[320px]">
      {/* Connection Handles */}
      <Handle type="target" position={Position.Top} className="w-2 h-2 !bg-primary" />
      <Handle type="source" position={Position.Bottom} className="w-2 h-2 !bg-primary" />

      {/* Header */}
      <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-secondary">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-foreground truncate">{data.name}</div>
          <div className="text-xs text-muted-foreground truncate">{data.schema}</div>
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {/* Feature III: Relationship Badge */}
          {data.relationshipCount !== undefined && data.onBadgeClick && (
            <RelationshipBadge
              count={data.relationshipCount}
              onClick={() => data.onBadgeClick(data.id)}
              isActive={data.isBadgeActive || false}
            />
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => data.onViewDetails(data.id)}
            className="h-7 w-7 p-0"
            title="View full table details"
          >
            <Eye className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Columns */}
      <div className="px-3 py-2">
        {/* Visible Columns */}
        <div className="space-y-1">
          {columnsToShow.map((column) => {
            // Feature I: Get relationships for this column
            const columnRelationships = data.relationshipLookup?.get(column.id) || []
            const hasRelationships = columnRelationships.length > 0

            return (
              <div
                key={column.id}
                className={`flex items-start justify-between text-xs py-0.5 group transition-colors ${
                  hasRelationships ? "cursor-pointer hover:bg-primary/5" : ""
                }`}
                onMouseEnter={() => {
                  if (hasRelationships && data.onColumnHover) {
                    data.onColumnHover(column.id, columnRelationships)
                  }
                }}
                onMouseLeave={() => {
                  if (hasRelationships && data.onColumnHover) {
                    data.onColumnHover(null, [])
                  }
                }}
              >
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <span className="text-sm flex-shrink-0">{getColumnIcon(column)}</span>
                  <span
                    className={`truncate font-mono ${
                      column.isPrimaryKey
                        ? "text-primary font-semibold"
                        : column.isForeignKey
                          ? "text-chart-2 font-medium"
                          : "text-foreground"
                    }`}
                  >
                    {column.name}
                  </span>
                </div>
                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                  <span className="text-muted-foreground text-[10px] font-mono">
                    {column.dataType.length > 12 ? column.dataType.substring(0, 12) + "..." : column.dataType}
                  </span>
                  <button
                    onClick={(e) => toggleColumnVisibility(column.id, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary"
                    title="Hide column"
                  >
                    <EyeOff className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Hidden Columns (shown when expanded) */}
        {isExpanded && hasVisibilityPreferences && hiddenColumnsList.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="text-[10px] text-muted-foreground uppercase font-semibold mb-2">Hidden Columns</div>
            <div className="space-y-1">
              {hiddenColumnsList.map((column) => {
                const columnRelationships = data.relationshipLookup?.get(column.id) || []
                const hasRelationships = columnRelationships.length > 0

                return (
                  <div
                    key={column.id}
                    className={`flex items-start justify-between text-xs py-0.5 group opacity-50 hover:opacity-100 transition-all ${
                      hasRelationships ? "cursor-pointer hover:bg-primary/5" : ""
                    }`}
                    onMouseEnter={() => {
                      if (hasRelationships && data.onColumnHover) {
                        data.onColumnHover(column.id, columnRelationships)
                      }
                    }}
                    onMouseLeave={() => {
                      if (hasRelationships && data.onColumnHover) {
                        data.onColumnHover(null, [])
                      }
                    }}
                  >
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="text-sm flex-shrink-0">{getColumnIcon(column)}</span>
                      <span className="truncate font-mono text-muted-foreground">{column.name}</span>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <span className="text-muted-foreground text-[10px] font-mono">
                        {column.dataType.length > 12 ? column.dataType.substring(0, 12) + "..." : column.dataType}
                      </span>
                      <button
                        onClick={(e) => toggleColumnVisibility(column.id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-primary"
                        title="Show column"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        {hiddenCount > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 py-1 rounded hover:bg-secondary transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronDown className="w-3 h-3" />
                Show less
              </>
            ) : (
              <>
                <ChevronRight className="w-3 h-3" />
                {hasVisibilityPreferences
                  ? `+${hiddenCount} more (${hiddenColumnsList.length} hidden)`
                  : `+${hiddenCount} more columns`}
              </>
            )}
          </button>
        )}
      </div>

      {/* Footer Info */}
      {data.description && (
        <div className="px-3 py-2 border-t border-border bg-secondary/50">
          <p className="text-xs text-muted-foreground line-clamp-2">{data.description}</p>
        </div>
      )}
    </div>
  )
}

export default TableNode
